import { BufferGeometry, Material, Vector3 } from 'three';
import { markRaw } from 'vue';
import { vector3 } from '@/domain/geometry/vector3';
import type { AnnotationGroup } from '../types';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import { isMeshNode, type MeshNode } from '@/lib/threeScene/nodes/mesh';
import { isLineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import type { NodeId } from '@/lib/threeScene/nodeId';
import type { ObjectNode } from '@/lib/threeScene/nodes/object';
import { HipSpineLandmarkName } from '@/domain/anatomy/hipSpineLandmarks';
import { rebuildFemurHeadCircleGeometry } from './femurHeadCircle';
import { landmarkToImagePoint } from '@/views/3d-planner/hipSpine/hipSpineCoordinates';

/**
 * Build the connecting-line geometry for an ordered list of landmarks:
 * each consecutive pair becomes one line segment, in image-pixel world space
 * (x right, y flipped). Returns a plain BufferGeometry — callers `markRaw` it.
 */
export function connectingLineGeometry(
    landmarks: HipSpineLandmarkApiObject[],
    imageSize: { width: number; height: number },
): BufferGeometry {
    const points: Vector3[] = landmarks.map((landmark) => {
        const p = landmarkToImagePoint(landmark, imageSize);
        return vector3(p.x, p.y, 0);
    });
    const vertices: Vector3[] = [];
    for (let index = 0; index < points.length - 1; index++) {
        vertices.push(points[index], points[index + 1]);
    }
    return new BufferGeometry().setFromPoints(vertices);
}

/**
 * The single definition of "is a landmark marker": the `landmark-` id prefix
 * that `buildAnnotationGroups` writes.
 */
export function isLandmarkMarkerNode(node: ObjectNode): node is MeshNode {
    return isMeshNode(node) && idHasPrefix(node.id, 'landmark-');
}

/** Collect the ordered landmark objects from a group that are present in `lmByName`. */
function collectGroupLandmarks(
    group: AnnotationGroup,
    lmByName: Map<string, HipSpineLandmarkApiObject>,
): HipSpineLandmarkApiObject[] {
    const out: HipSpineLandmarkApiObject[] = [];
    for (const child of group.children) {
        if (!isLandmarkMarkerNode(child)) continue;
        const named = child.name;
        if (!named) continue;
        const lm = lmByName.get(named);
        if (lm) out.push(lm);
    }
    return out;
}

/** Rebuild connecting-line BufferGeometries for a group (skips femur-head dashed circle). */
function rebuildGroupLineGeometry(
    group: AnnotationGroup,
    landmarks: HipSpineLandmarkApiObject[],
    imageSize: { width: number; height: number },
): void {
    for (const child of group.children) {
        if (
            isLineSegmentsNode(child) &&
            idHasPrefix(child.id, 'line-') &&
            !idHasPrefix(child.id, 'line-femur-head-')
        ) {
            child.geometry?.dispose();
            child.geometry = markRaw(connectingLineGeometry(landmarks, imageSize));
        }
    }
}

/**
 * Apply landmark position changes to the scene during a drag:
 *  - Update each landmark mesh node's transform from `resolvedLandmarks`.
 *  - Rebuild line BufferGeometries inside each group from current resolved positions.
 *
 * The femur head dashed circle (line-femur-head-N) is NOT rebuilt here — it has its own
 * sync function (applyFemurCircleToScene in Task 12) because its geometry depends on
 * a center + radius rather than a polyline.
 */
export function applyLandmarkPositionToScene(
    annotationGroups: AnnotationGroup[],
    resolvedLandmarks: HipSpineLandmarkApiObject[],
    imageSize: { width: number; height: number },
): void {
    const lmByName = new Map(resolvedLandmarks.map((lm) => [lm.name, lm]));

    for (const group of annotationGroups) {
        for (const child of group.children) {
            if (isLandmarkMarkerNode(child)) {
                const lm = child.name ? lmByName.get(child.name) : undefined;
                if (!lm) continue;
                const p = landmarkToImagePoint(lm, imageSize);
                child.transform.setPosition(p.x, p.y, 0);
            }
        }
        const groupLandmarks = collectGroupLandmarks(group, lmByName);
        if (groupLandmarks.length >= 2) rebuildGroupLineGeometry(group, groupLandmarks, imageSize);
    }
}

/** NodeIds may be number or string; only string ids carry our semantic prefixes. */
function idHasPrefix(id: NodeId, prefix: string): boolean {
    return typeof id === 'string' && id.startsWith(prefix);
}

export type DragOpacityTarget = { kind: 'landmark'; name: string } | { kind: 'femur'; head: 1 | 2 };

// Remembers each dimmed node's original (shared) material so a restore can swap
// it back. Keyed by the node object; entries are dropped on restore.
const dimmedOriginals = new WeakMap<object, Material>();

function findDragNode(groups: AnnotationGroup[], target: DragOpacityTarget) {
    for (const group of groups) {
        for (const child of group.children) {
            if (target.kind === 'landmark') {
                if (isLandmarkMarkerNode(child) && child.name === target.name) {
                    return child;
                }
            } else if (isLineSegmentsNode(child) && child.id === `line-femur-head-${target.head}`) {
                return child;
            }
        }
    }
    return undefined;
}

/**
 * Dim (or, with `opacity >= 1`, restore) the dragged annotation's material.
 * Because dot/line materials are shared per colour, dimming swaps the node to a
 * transparent clone and restoring swaps the shared original back — leaving
 * sibling annotations untouched.
 */
export function setAnnotationDragOpacity(
    groups: AnnotationGroup[],
    target: DragOpacityTarget,
    opacity: number,
): void {
    const node = findDragNode(groups, target);
    if (!node || !(node.material instanceof Material)) return;

    if (opacity >= 1) {
        const original = dimmedOriginals.get(node);
        if (!original) return;
        const clone = node.material;
        node.material = markRaw(original);
        dimmedOriginals.delete(node);
        if (clone !== original) clone.dispose();
        return;
    }

    if (dimmedOriginals.has(node)) return;
    dimmedOriginals.set(node, node.material);
    const dim = node.material.clone();
    dim.transparent = true;
    dim.opacity = opacity;
    node.material = markRaw(dim);
}

/**
 * Apply femur head circle changes to the scene during a drag:
 *  - For each head (1, 2), find the HJC landmark center and the line node `line-femur-head-N`,
 *    rebuild its dashed-circle geometry from the new center + radius.
 *
 * Used during landmark drag (when an HJC moves) and during femur-resize drag (when the
 * radius changes). Skips silently if the HJC or line node is absent.
 */
export function applyFemurCircleToScene(
    annotationGroups: AnnotationGroup[],
    resolvedLandmarks: HipSpineLandmarkApiObject[],
    imageSize: { width: number; height: number },
    radii: { 1: number; 2: number },
): void {
    for (const head of [1, 2] as const) {
        const hjcName = head === 1 ? HipSpineLandmarkName.HJC1 : HipSpineLandmarkName.HJC2;
        const lm = resolvedLandmarks.find((l) => l.name === hjcName);
        if (!lm) continue;
        const center = landmarkToImagePoint(lm, imageSize);
        for (const group of annotationGroups) {
            for (const child of group.children) {
                if (isLineSegmentsNode(child) && child.id === `line-femur-head-${head}`) {
                    rebuildFemurHeadCircleGeometry(child, center, radii[head]);
                }
            }
        }
    }
}
